import { type FormEvent, useEffect, useMemo, useState } from 'react'
import { createJoke, deleteJoke, getJokes, updateJoke } from '../api/jokesApi'
import type { Joke } from '../types'

type JokeFormState = {
  content: string
  author: string
}

const emptyForm: JokeFormState = {
  content: '',
  author: '',
}

const maxContentLength = 500
const maxAuthorLength = 120

export function AdminPage() {
  const [jokes, setJokes] = useState<Joke[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [newJokeForm, setNewJokeForm] = useState<JokeFormState>(emptyForm)
  const [creating, setCreating] = useState(false)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [editForm, setEditForm] = useState<JokeFormState>(emptyForm)
  const [savingId, setSavingId] = useState<string | null>(null)
  const [deletingId, setDeletingId] = useState<string | null>(null)

  useEffect(() => {
    const fetchJokes = async () => {
      try {
        const data = await getJokes(0, 200)
        setJokes(data)
      } catch (err) {
        console.error(err)
        setError('Failed to load jokes. Please refresh and try again.')
      } finally {
        setLoading(false)
      }
    }

    fetchJokes()
  }, [])

  const activeEditJoke = useMemo(() => jokes.find((joke) => joke.id === editingId) ?? null, [editingId, jokes])

  const handleCreate = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    const trimmedContent = newJokeForm.content.trim()
    const trimmedAuthor = newJokeForm.author.trim()

    if (!trimmedContent) {
      setError('Content is required to create a joke.')
      return
    }

    if (trimmedContent.length > maxContentLength) {
      setError(`Content must be ${maxContentLength} characters or fewer.`)
      return
    }

    if (trimmedAuthor.length > maxAuthorLength) {
      setError(`Author must be ${maxAuthorLength} characters or fewer.`)
      return
    }

    setCreating(true)
    setError(null)

    try {
      const created = await createJoke({
        content: trimmedContent,
        author: trimmedAuthor ? trimmedAuthor : undefined,
      })
      setJokes((prev) => [created, ...prev])
      setNewJokeForm(emptyForm)
    } catch (err) {
      console.error(err)
      setError('Failed to create joke. Please try again.')
    } finally {
      setCreating(false)
    }
  }

  const handleStartEdit = (joke: Joke) => {
    setError(null)
    setEditingId(joke.id)
    setEditForm({
      content: joke.content,
      author: joke.author ?? '',
    })
  }

  const handleCancelEdit = () => {
    setEditingId(null)
    setEditForm(emptyForm)
  }

  const handleUpdate = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (!editingId || !activeEditJoke) {
      return
    }

    const trimmedContent = editForm.content.trim()
    const trimmedAuthor = editForm.author.trim()

    if (!trimmedContent) {
      setError('Content cannot be empty.')
      return
    }

    if (trimmedContent.length > maxContentLength) {
      setError(`Content must be ${maxContentLength} characters or fewer.`)
      return
    }

    if (trimmedAuthor.length > maxAuthorLength) {
      setError(`Author must be ${maxAuthorLength} characters or fewer.`)
      return
    }

    const payload: { content?: string; author?: string | null } = {}

    if (trimmedContent !== activeEditJoke.content) {
      payload.content = trimmedContent
    }

    const normalizedAuthor = trimmedAuthor || null
    if (normalizedAuthor !== (activeEditJoke.author ?? null)) {
      payload.author = normalizedAuthor
    }

    if (Object.keys(payload).length === 0) {
      setEditingId(null)
      return
    }

    setSavingId(editingId)
    setError(null)

    try {
      const updated = await updateJoke(editingId, payload)
      setJokes((prev) => prev.map((joke) => (joke.id === editingId ? updated : joke)))
      setEditingId(null)
      setEditForm(emptyForm)
    } catch (err) {
      console.error(err)
      setError('Failed to update joke. Please try again.')
    } finally {
      setSavingId(null)
    }
  }

  const handleDelete = async (id: string) => {
    const confirmed = window.confirm('Are you sure you want to delete this joke?')
    if (!confirmed) {
      return
    }

    setDeletingId(id)
    setError(null)

    try {
      await deleteJoke(id)
      setJokes((prev) => prev.filter((joke) => joke.id !== id))
    } catch (err) {
      console.error(err)
      setError('Failed to delete joke. Please try again.')
    } finally {
      setDeletingId(null)
    }
  }

  return (
    <section className="page admin-page">
      <header>
        <h1>Jokes Admin</h1>
        <p>Manage your jokes library: create, update, and remove entries.</p>
      </header>

      {error && <p className="error" role="alert">{error}</p>}

      <form className="joke-form" onSubmit={handleCreate}>
        <h2>Add a new joke</h2>
        <label>
          Content
          <textarea
            name="content"
            value={newJokeForm.content}
            onChange={(event) => setNewJokeForm((prev) => ({ ...prev, content: event.target.value }))}
            maxLength={maxContentLength}
            required
          />
        </label>
        <label>
          Author (optional)
          <input
            name="author"
            value={newJokeForm.author}
            onChange={(event) => setNewJokeForm((prev) => ({ ...prev, author: event.target.value }))}
            maxLength={maxAuthorLength}
          />
        </label>
        <button type="submit" disabled={creating}>
          {creating ? 'Saving...' : 'Add Joke'}
        </button>
      </form>

      <section className="table-wrapper" aria-live="polite">
        {loading ? (
          <p>Loading jokes...</p>
        ) : (
          <table>
            <caption className="sr-only">Existing jokes</caption>
            <thead>
              <tr>
                <th scope="col">Content</th>
                <th scope="col">Author</th>
                <th scope="col">Created</th>
                <th scope="col">Updated</th>
                <th scope="col" className="actions-col">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {jokes.length === 0 && (
                <tr>
                  <td colSpan={5} className="empty">
                    No jokes yet. Add one above!
                  </td>
                </tr>
              )}

              {jokes.map((joke) => (
                <tr key={joke.id} className={editingId === joke.id ? 'editing' : undefined}>
                  <td data-title="Content">
                    {editingId === joke.id ? (
                      <form className="inline-form" onSubmit={handleUpdate}>
                        <textarea
                          value={editForm.content}
                          onChange={(event) => setEditForm((prev) => ({ ...prev, content: event.target.value }))}
                          maxLength={maxContentLength}
                          required
                        />
                        <label>
                          Author
                          <input
                            value={editForm.author}
                            onChange={(event) => setEditForm((prev) => ({ ...prev, author: event.target.value }))}
                            maxLength={maxAuthorLength}
                          />
                        </label>
                        <div className="inline-actions">
                          <button type="submit" disabled={savingId === joke.id}>
                            {savingId === joke.id ? 'Saving...' : 'Save'}
                          </button>
                          <button type="button" onClick={handleCancelEdit} className="secondary">
                            Cancel
                          </button>
                        </div>
                      </form>
                    ) : (
                      <>
                        <p>{joke.content}</p>
                        {joke.author && <p className="muted">{joke.author}</p>}
                      </>
                    )}
                  </td>
                  <td data-title="Author">{joke.author ?? '—'}</td>
                  <td data-title="Created">{new Date(joke.createdUtc).toLocaleString()}</td>
                  <td data-title="Updated">{new Date(joke.updatedUtc).toLocaleString()}</td>
                  <td data-title="Actions" className="actions-col">
                    {editingId === joke.id ? null : (
                      <div className="row-actions">
                        <button type="button" onClick={() => handleStartEdit(joke)}>
                          Edit
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDelete(joke.id)}
                          className="danger"
                          disabled={deletingId === joke.id}
                        >
                          {deletingId === joke.id ? 'Deleting...' : 'Delete'}
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    </section>
  )
}
